// src/pages/AdminUsersPage.js

import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  updateDoc,
  doc,
} from 'firebase/firestore'
import { db } from '../firebase'
import {
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Box,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil
import { useTranslation } from 'react-i18next'

// TabPanel bileşeni: Her sekmede gösterilecek içeriği sarmak için
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

export default function AdminUsersPage() {
  const { t } = useTranslation()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [searchTerm, setSearchTerm] = useState('') // Arama metni

  // Sekme durumu
  const [tabValue, setTabValue] = useState(0)

  // Admin kontrolü
  const [isAdmin, setIsAdmin] = useState(false)

  // Auth
  const { currentUser, userRole } = useAuth()

  // Firestore'dan kullanıcıları çek
  useEffect(() => {
    const usersRef = collection(db, 'users')
    const q = query(usersRef, orderBy('createdAt', 'asc'))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      setUsers(userList)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    // Admin kontrolü
    if (userRole === 'admin') {
      setIsAdmin(true)
    } else {
      setIsAdmin(false)
    }
  }, [currentUser, userRole])

  // Mevcut admin sayısını hesaplamak için yardımcı fonksiyon
  const getAdminCount = () => {
    return users.filter((u) => u.role === 'admin').length
  }

  // Rol güncelle
  const handleRoleChange = async (userId, newRole) => {
    try {
      const userToChange = users.find((u) => u.id === userId)
      if (!userToChange) return

      const adminCount = getAdminCount()

      if (
        adminCount === 1 &&
        userToChange.role === 'admin' &&
        newRole !== 'admin'
      ) {
        setSnackbar({
          open: true,
          message: t('adminUsers.adminWarning'),
          severity: 'error',
        })
        return
      }

      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { role: newRole })

      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Rol güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleError'),
        severity: 'error',
      })
    }
  }

  // Durum güncelle
  const handleStatusChange = async (userId, newStatus) => {
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { status: newStatus })

      setSnackbar({
        open: true,
        message: t('adminUsers.statusUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Durum güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.statusError'),
        severity: 'error',
      })
    }
  }

  const handleVerifyEmail = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { emailVerified: true })

      setSnackbar({
        open: true,
        message: t('adminUsers.emailVerifiedUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Email doğrulama hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.emailVerifiedError'),
        severity: 'error',
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Kullanıcı arama
  const filteredUsers = users.filter((user) => {
    const text = searchTerm.toLowerCase()
    const emailMatch = user.email?.toLowerCase().includes(text)
    const nicknameMatch = user.nickname?.toLowerCase().includes(text)
    const firstNameMatch = user.firstName?.toLowerCase().includes(text)
    const lastNameMatch = user.lastName?.toLowerCase().includes(text)
    return emailMatch || nicknameMatch || firstNameMatch || lastNameMatch
  })

  // Aktif ve pasif kullanıcıları ayır
  const verifiedUsers = filteredUsers.filter((user) => user.emailVerified === true)
  const activeUsers = verifiedUsers.filter((user) => user.status === 'active')
  const passiveUsers = verifiedUsers.filter((user) => user.status === 'passive')
  const unverifiedUsers = filteredUsers.filter((user) => user.emailVerified !== true)

  // Sekme değişimi
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>{t('adminUsers.loading')}</Typography>
      </Container>
    )
  }

  if (!isAdmin) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{t('adminUsers.noAccess')}</Alert>
      </Container>
    )
  }

  return (
    <Container sx={{ mt: 4, backgroundColor: '#f9f9f9', padding: 4, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <PeopleAltIcon sx={{ mr: 1 }} />
        {t('adminUsers.pageTitle')}
      </Typography>

      {/* Arama alanı */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('adminUsers.searchLabel')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Sekmeler (Active - Passive) */}
      <Tabs value={tabValue} onChange={handleTabChange} centered>
        <Tab label={t('adminUsers.activeTab')} />
        <Tab label={t('adminUsers.passiveTab')} />
        <Tab label={t('adminUsers.unverifiedTab')} />
      </Tabs>

      {/* Active Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={0}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.activeTab')} />
          <CardContent>
            <List>
              {activeUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    p: 1, 
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={user.email}
                    secondary={
                      <>
                        {user.nickname && (
                          <span>{t('adminUsers.nickname')}: {user.nickname} — </span>
                        )}
                        {user.firstName && user.lastName && (
                          <span>
                            {t('adminUsers.fullName')}: {user.firstName} {user.lastName}
                          </span>
                        )}
                      </>
                    }
                    sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                  />

                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 110 }}
                    >
                      <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.roleLabel')}
                        value={user.role || t('adminUsers.user')}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      >
                        <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                        <MenuItem value="observer">{t('adminUsers.observer')}</MenuItem>
                        <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 120 }}
                    >
                      <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.statusLabel')}
                        value={user.status || t('adminUsers.active')}
                        onChange={(e) =>
                          handleStatusChange(user.id, e.target.value)
                        }
                      >
                        <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                        <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Passive Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={1}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.passiveTab')} />
          <CardContent>
            <List>
              {passiveUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.email}
                    secondary={
                      <>
                        {user.nickname && (
                          <span>{t('adminUsers.nickname')}: {user.nickname} — </span>
                        )}
                        {user.firstName && user.lastName && (
                          <span>
                            {t('adminUsers.fullName')}: {user.firstName} {user.lastName}
                          </span>
                        )}
                      </>
                    }
                    sx={{ flex: 1 }}
                  />

                  {/* Rol Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 110 }}
                  >
                    <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.roleLabel')}
                      value={user.role || t('adminUsers.user')}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    >
                      <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                      <MenuItem value="observer">{t('adminUsers.observer')}</MenuItem>
                      <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Kullanıcı Durum Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 120 }}
                  >
                    <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.statusLabel')}
                      value={user.status || t('adminUsers.active')}
                      onChange={(e) =>
                        handleStatusChange(user.id, e.target.value)
                      }
                    >
                      <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                      <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                    </Select>
                  </FormControl>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Email Doğrulanmamış Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={2}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.unverifiedTab')} />
          <CardContent>
            <List>
              {unverifiedUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    p: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={user.email}
                    secondary={
                      <>
                        {user.nickname && (
                          <span>{t('adminUsers.nickname')}: {user.nickname} — </span>
                        )}
                        {user.firstName && user.lastName && (
                          <span>
                            {t('adminUsers.fullName')}: {user.firstName} {user.lastName}
                          </span>
                        )}
                      </>
                    }
                    sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                  />

                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleVerifyEmail(user.id)}
                  >
                    {t('adminUsers.verifyEmailButton')}
                  </Button>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
